import { useEffect, useState } from "react"
import { api } from "../env"
import Loading from "./loading"
import useIsMobile from "../hooks/isMobile"
import ErrorFrame from "./error"

const TABLE_STATE = {
    LOADING: 'Loading',
    SHOWING: 'Showing',
    ERROR: 'Error',
    NO_DATA: 'No_Data'
}

export const TABLE_STYLE = {
    CARD: 'card',
    OUTLINE: 'outline-card'
}

export default function MTable({path, queryParams, mapper, rawData, itemsPerPage, onClick, showHeader, selectable, style, cardView, cardViewSettings, autoHideNavigation, customUI, onMeta, refreshToken, emptyState}) {

    const [state, setState] = useState(TABLE_STATE.LOADING)
    const [data, setData] = useState([])
    const [page, setPage] = useState(undefined)
    const [templateColumns, setTemplateColumns] = useState('1fr')
    const [selected, setSelected] = useState(undefined)
    const [isCards, setIsCards] = useState(false)

    const isMobile = useIsMobile(1150)
    useEffect(() => setIsCards(isMobile || selectable?.cards === true || cardView), [isMobile, selectable?.cards, cardView])

    useEffect(() => {
        if(path) fetchData(1)
    }, [path])

    useEffect(() => {
        if(page) fetchData(page.page, page.limit, false)
    }, [queryParams])

    useEffect(() => {
        if (selectable && selectable?.preselected?.length > 0 && !selected) {
            setSelected(selectable.preselected)
        }
        if (selectable && selectable?.externallyManagedSelected) {
            setSelected(selectable.externallyManagedSelected.filter(it => it !== undefined))
        }
    }, [selectable])

    useEffect(() => {
        if(!rawData) return
        setData(rawData)
        setState(rawData?.length == 0 ? TABLE_STATE.NO_DATA : TABLE_STATE.SHOWING)
    }, [rawData])

    useEffect(() => {
        if (!mapper) return
        let widths = []
        mapper.columns.forEach(col => widths.push(col?.width ?? '1fr'))
        setTemplateColumns(widths.join(' '))
    }, [mapper])

    useEffect(() => {
        if (onMeta && page) onMeta(page)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page])

    // Silent so existing rows stay visible during the request — prevents scroll jump
    // after inline mutations. `page` guard avoids double-fetch on first mount.
    useEffect(() => {
        if (path && page) fetchData(page.page, page.limit, true)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshToken])

    const fetchData = (page, count, silent) => {
        if (!silent) setState(TABLE_STATE.LOADING)
        let limit = count ? count : (itemsPerPage ?? 10)
        api.get(path, { params: { page: page, limit: limit, ...queryParams}})
            .then(res => {
                const items = res.data.data
                const meta = res.data.meta
                // Requested page no longer exists — snap to the last existing page.
                // Guard on pages > 0 so a truly empty list still falls through to NO_DATA.
                if (items.length === 0 && meta?.pages > 0 && page > meta.pages) {
                    fetchData(meta.pages, count, silent)
                    return
                }
                setData(items)
                setPage(meta)
                setState(items.length > 0 ? TABLE_STATE.SHOWING : TABLE_STATE.NO_DATA)
            })
            .catch(err => {
                setState(TABLE_STATE.ERROR)
            })
    }

    const retry = () => {
        fetchData(0)
    }

    const smartCompareSelectionIndex = (e, item) => {
        if (selectable?.idMapper) return selectable.idMapper(e) === selectable.idMapper(item)
        return e.id === item.id
    }

    const onRowClicked = (item) => {
        let tempSelected = selected ? [...selected] : []
        if (onClick) onClick(item)
        else if (selectable) {
            let exist = tempSelected.findIndex(e => smartCompareSelectionIndex(e, item))
            if (exist == -1) {
                if (selectable.multiple || tempSelected.length == 0) tempSelected = [...tempSelected, item]
                else tempSelected = [item]
            }
            else tempSelected.splice(exist, 1)
            setSelected(tempSelected)
            selectable?.onChange(tempSelected)
        }
    }

    const executeAction = (e, action, item) => {
        e.stopPropagation()
        action?.onClick(item)
    }

    const getBorderRadius = (idx) => {
        if(isCards) return 'var(--card-radius)'
        if (idx == 0 && data.length > 1) return 'var(--card-radius) var(--card-radius) 0 0'
        else if (idx == data.length - 1 && data.length > 1) return '0 0 var(--card-radius) var(--card-radius)'
        else if (idx == 0 || idx == data.length - 1) return 'var(--card-radius)'
        else return '0'
    }

    return(
        <main>
            {state == TABLE_STATE.SHOWING && <div className={`${isCards ? 'grid gap-xs' : ''}`} style={{gridTemplateColumns: `repeat(auto-fill, minmax(${cardViewSettings?.minWidth ?? '300px'}, 1fr))`}}>
                {!isCards && (showHeader == undefined || showHeader) && mapper && <MTableHeader columns={mapper?.columns} template={templateColumns}/>}
                {data.filter(it => !it?.isDeleted).map((item, itemIdx) => 
                    <div
                        key={itemIdx} 
                        className={`grid center gap-xs ${style ? style : 'card'} ${onClick || selectable ? 'clickable' : ''} ${selected && selected.findIndex(e => smartCompareSelectionIndex(e, item)) != -1 ? 'shaded-primary' : ''}`}
                        style={{borderRadius: getBorderRadius(itemIdx), gridTemplateColumns: isCards ? (customUI ? '1fr' : 'max-content auto') : templateColumns, minWidth: 0}}
                        onClick={() => { onRowClicked(item) }}
                    >
                        {!customUI && mapper && mapper?.columns.map((col, colIdx) =>
                            col.render ? (
                                isCards && col.label ? (<>
                                    <p className="key-item">{col.label}</p>
                                    <div key={`${itemIdx}/${colIdx}`} className="mtable-card">{col.render(item)}</div>
                                </>) : (
                                    <div key={`${itemIdx}/${colIdx}`} style={isCards ? {gridColumn: '1 / -1'} : undefined}>{col.render(item)}</div>
                                )
                            ) : (<>
                                {isCards && <p className="key-item">{col.label}</p>}
                                {col?.value && <p
                                    key={`${itemIdx}/${colIdx}`}
                                    className={`
                                        ${isCards ? 'value-item' : ''}
                                        ${colIdx == 0 || isCards ? '' : 'text-center'}
                                        ${col?.style ?? ''}
                                        ${col?.dynamicStyle?.(item) ?? ''}
                                    `}
                                >
                                    {col.value(item)}
                                </p>}
                                {col?.actions && <div className="flex justify-center gap-xs">
                                    {col.actions.map(action => <button className={action?.style?.(item) ?? 'text'} disabled={action?.style?.(item).includes('disabled')} onClick={(e) => executeAction(e, action, item)}>
                                        {action.label}
                                    </button>)}
                                </div>}
                            </>)
                        )}
                        {customUI && customUI(item, itemIdx)}
                    </div>
                )}
            </div>}
            {state == TABLE_STATE.LOADING && <Loading/>}
            {state == TABLE_STATE.ERROR && <ErrorFrame onAction={retry}/>}
            {state == TABLE_STATE.NO_DATA && (emptyState ?? <div className="dashed-card standard-padding">
                <p className="card-title">No Data</p>
            </div>)}
            {(state == TABLE_STATE.SHOWING || state == TABLE_STATE.LOADING) && page && !(autoHideNavigation && page.pages == 1) && <MTablePager page={page} navigateToPage={(idx) => fetchData(idx)}/> }
        </main>
    )
}

function MTableHeader({columns, template}) {
    return(
        <div className="grid gap-xs" style={{gridTemplateColumns: template, padding: 'var(--xs) var(--m) var(--xs) var(--xs)'}}>
            {columns.map((col, idx) => <p className={`card-label neutral medium uppercase ${col.align === 'left' ? '' : col.align === 'center' ? 'text-center' : col.render ? '' : idx == 0 ? '' : 'text-center'}`}>
                {col.label}
            </p>)}
        </div>
    )
}

function MTablePager({page, navigateToPage}) {
    const current = page.page
    const total = page.pages

    const windowStart = Math.max(1, current - 3)
    const windowEnd = Math.min(total, current + 3)
    const visiblePages = []
    for (let i = windowStart; i <= windowEnd; i++) visiblePages.push(i)

    return (
        <div className="flex gap-xxs vi-spacer-s" style={{alignItems: 'center'}}>
            <button
                className={`outline with-icon only ${current === 1 ? 'disabled' : ''}`}
                disabled={current === 1}
                onClick={() => navigateToPage(1)}
            ><span className="material-symbols-outlined scale-xs">first_page</span></button>
            <button
                className={`outline with-icon leading ${current === 1 ? 'disabled' : ''}`}
                disabled={current === 1}
                onClick={() => navigateToPage(current - 1)}
            ><span className="material-symbols-outlined scale-xs">chevron_backward</span> Back</button>
            {windowStart > 1 && <span style={{padding: '0 4px'}}>...</span>}
            {visiblePages.map(num => (
                <button
                    key={num}
                    className={current === num ? 'solid' : 'outline'}
                    onClick={() => navigateToPage(num)}
                >{num}</button>
            ))}
            {windowEnd < total && <span style={{padding: '0 4px'}}>...</span>}
            <button
                className={`outline with-icon trailing ${current === total ? 'disabled' : ''}`}
                disabled={current === total}
                onClick={() => navigateToPage(current + 1)}
            >Next <span className="material-symbols-outlined scale-xs">chevron_forward</span></button>
            <button
                className={`outline with-icon only ${current === total ? 'disabled' : ''}`}
                disabled={current === total}
                onClick={() => navigateToPage(total)}
            ><span className="material-symbols-outlined scale-xs">last_page</span></button>
        </div>
    )
}